import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Globe, Bot, Cloud, Mail, GraduationCap, Fish, AlertTriangle, ArrowRight } from 'lucide-react';

interface ProtectSummary {
  breaches_found: number;
  shadow_ai_discoveries: number;
  high_risk_posture_findings: number;
  domains_monitored: number;
  training_campaigns: number;
  phishing_campaigns: number;
}

const QUICK_ACTIONS = [
  { label: 'Dark Web Scan', icon: Globe, path: '/dark-web', desc: 'Check for breaches' },
  { label: 'Shadow AI', icon: Bot, path: '/shadow-ai', desc: 'Discover unauthorized AI' },
  { label: 'SaaS Posture', icon: Cloud, path: '/saas-posture', desc: 'Audit SaaS security' },
  { label: 'DMARC', icon: Mail, path: '/dmarc', desc: 'Email authentication' },
  { label: 'Training', icon: GraduationCap, path: '/training', desc: 'Security awareness' },
  { label: 'Phishing Sim', icon: Fish, path: '/phishing', desc: 'Test user resilience' },
];

export function DashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ProtectSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/protect/dashboard', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(res => setSummary(res?.data ?? null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeThreats = (summary?.breaches_found ?? 0) + (summary?.high_risk_posture_findings ?? 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-emerald-100">
          <Shield className="h-6 w-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Protection Overview</h1>
          <p className="text-sm text-gray-500">Security posture at a glance</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          label="Active Threats"
          value={loading ? '—' : String(activeThreats)}
          color={activeThreats > 0 ? 'text-red-600' : 'text-emerald-600'}
          icon={<AlertTriangle className="h-5 w-5 text-red-500" />}
        />
        <SummaryCard
          label="Domains Monitored"
          value={loading ? '—' : String(summary?.domains_monitored ?? 0)}
          color="text-blue-600"
          icon={<Mail className="h-5 w-5 text-blue-500" />}
        />
        <SummaryCard
          label="Breaches Found"
          value={loading ? '—' : String(summary?.breaches_found ?? 0)}
          color={summary?.breaches_found ? 'text-orange-600' : 'text-gray-900'}
          icon={<Globe className="h-5 w-5 text-orange-500" />}
        />
        <SummaryCard
          label="Shadow AI Detections"
          value={loading ? '—' : String(summary?.shadow_ai_discoveries ?? 0)}
          color="text-purple-600"
          icon={<Bot className="h-5 w-5 text-purple-500" />}
        />
      </div>

      {/* Campaigns Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-1">
            <GraduationCap className="h-4 w-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Training Campaigns</span>
          </div>
          <span className="text-2xl font-bold text-gray-900">{loading ? '—' : summary?.training_campaigns ?? 0}</span>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-1">
            <Fish className="h-4 w-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phishing Campaigns</span>
          </div>
          <span className="text-2xl font-bold text-gray-900">{loading ? '—' : summary?.phishing_campaigns ?? 0}</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.path}
              type="button"
              onClick={() => navigate(a.path)}
              className="group text-left bg-white rounded-xl border border-gray-200 p-4 hover:border-emerald-300 hover:shadow-sm transition-all"
            >
              <a.icon className="h-5 w-5 text-emerald-600 mb-2" />
              <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                {a.label}
                <ArrowRight className="h-3 w-3 text-gray-400 group-hover:text-emerald-500 transition-colors" />
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{a.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color, icon }: { label: string; value: string; color: string; icon: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        {icon}
      </div>
      <span className={`text-3xl font-bold ${color}`}>{value}</span>
    </div>
  );
}
